<template>
  <wb-accordion-item
    class="slot-selection-box"
    :class="[
      getSelectionBoxClass('slot-selection-box'),
      'request-flow-selector',
    ]"
    :open="boxIsOpen"
    ref="accordionRef"
  >
    <wb-accordion-toggle class="slot-selection-box__toggle" @click="toggle">
      <div class="slot-selection-box__step">
        <span v-if="!showSuccessIcon">{{ step }}</span>
        <wb-icon
          v-else
          name="bds/checkmark/16"
          class="slot-selection-box__step--success"
        ></wb-icon>
      </div>
      <div class="slot-selection-box__label" :class="slotSelectionBoxLabelFont">
        <wb-icon name="time"></wb-icon>
        <span v-if="showToggleLabel">{{
          $t('schedule.request.selectionBoxes.time')
        }}</span>
      </div>
      <div class="slot-selection-box__selected-value">
        <span v-if="showSuccessIcon" class="wb-type-copy">
          {{ selectedValue }}
        </span>
      </div>
    </wb-accordion-toggle>
    <wb-accordion-section class="mobile-overflow-wb-disable">
      <wb-accordion-mobile-header @click="closeAccordion(emit)">{{
        $t('schedule.filter.back')
      }}</wb-accordion-mobile-header>
      <wb-accordion-content>
        <div
          class="slot-selection-box__label__in-modal"
          :class="slotSelectionBoxLabelFont"
          v-if="isMobile && !isMq4"
        >
          <div class="slot-selection-box__label__in-modal-top">
            <wb-icon
              id="slot-selection-box__label__in-modal-icon"
              name="time"
            ></wb-icon>
            <span
              class="slot-selection-box__label__in-modal-text wb-type-heading-s"
              >{{ $t('schedule.request.selectionBoxes.time') }}</span
            >
          </div>
          <div class="slot-selection-box__label__in-modal-bottom">
            <span
              class="wb-type-copy-strong slot-selection-box__label-in-modal-text"
              >{{ $t('schedule.request.selectionBoxes.time.subtitle') }}
            </span>
          </div>
        </div>
        <div class="slot-selection-box__datetime-picker">
          <datetime-picker
            :activeTextCarouselSlide="activeCalendarSlide"
            :dealerCalendar="dealerCalendar"
            :formLabel="$t('schedule.datepicker.start.label')"
            :labelPlaceholder="$t('schedule.datepicker.start.placeholder')"
            :locale="localeCode"
            :selectedDates="selectedDates"
            :selectedSlot="selectedSlot"
            :timeOptions="requestSlotsResponse"
            :timeOptionsLabel="$t('schedule.timeSlot.label')"
            :isMobile="isMobile"
            :requestMode="true"
            @changeDate="changeDate"
            @changeSheet="changeSheet"
            @selectedSlot="changeSlot"
            v-if="!isMobile"
          />
          <template v-if="isMobile">
            <calendar
              class="otb-date-picker__calendar mobile-normal-calendar"
              :activeCalendarPage="activeCalendarSlide"
              :dealerCalendar="dealerCalendar"
              :selectedDates="selectedDates"
              :locale="localeCode"
              :pages="3"
              :isOptional="true"
              @changeDate="changeDate"
              @changeSheet="changeCalendarSheet"
            >
              <template v-slot:default="{ availableSheets }">
                <text-carousel
                  class="otb-calendar-value wb-type-copy-secondary"
                  :activeSlide="activeCalendarSlide"
                  :infinite="false"
                  :options="availableSheets"
                  :numberOfSlides="3"
                  :variant="2"
                  @input="changeCalendarSheet"
                >
                </text-carousel>
              </template>
            </calendar>
            <calendar-period
              class="datetime-picker__period-list mobile-normal-timeslot"
              :timeOptions="requestSlotsResponse"
              :timeOptionsLabel="$t('schedule.timeSlot.label')"
              :selectedSlot="selectedSlot"
              :requestMode="true"
              @selectedSlot="changeSlot"
            />
          </template>
        </div>
        <div class="slot-selection-box__actions" v-if="!(isMobile && !isMq4)">
          <button
            type="button"
            class="wb-button wb-button--primary wb-button--medium"
            @click="apply"
          >
            {{ $t('schedule.request.selectionBoxes.apply') }}
          </button>
        </div>
        <div
          v-else
          class="slot-selection-box__actions-mobile"
          :style="footerTopStyle"
        >
          <button
            type="button"
            class="slot-selection-box__actions-mobile__cta wb-button wb-button--primary wb-button--medium"
            @click="apply"
          >
            {{ $t('schedule.request.selectionBoxes.apply') }}
          </button>
        </div>
      </wb-accordion-content>
    </wb-accordion-section>
  </wb-accordion-item>
</template>

<script lang="ts">
import {
  PropType,
  computed,
  defineComponent,
  onMounted,
  onUpdated,
  ref,
  watch,
} from 'vue';
import { i18n } from '../../../services/translation.service';

import useRequestSelectionBoxes from '../../../composables/request-selection-boxes.composable';
import { useStore } from '../../../composables/store.composable';

import {
  Calendar,
  CalendarPeriod,
  DatetimePicker,
  TextCarousel,
} from '../../index';

import {
  selectedRangeDate,
  selectedRangeTime,
} from '../../../mocks/range-date-picker.mock';

import DatetimePickerService from '../../../../../otbc/src/services/datetimePicker.service';
import {
  CalendarPeriodOption,
  DatesAvailabilityType,
} from '../../../models/entities/calendar.model';
import {
  RequestSelectionBoxesStatus,
  RequestSelectionBoxesStatusType,
  RequestSelectionBoxesStep,
} from '../../../models/entities/request-selection-boxes.model';
import { SlideInformation } from '../../../models/entities/text-carousel.model';

import { useFooterPosition } from '../../../../../otbc/src/composables/footer.composable';
import useCalendar from '../../../composables/calendar.composable';
import { useMediaQuery } from '../../../composables/media-query.composable';
import useRequestSessionStorage from '../../../composables/request-session.composable';
import { useSlotSelectionBox } from '../../../composables/slot-selection-box.composable';
import { TRACKING_EVENT_LABEL } from '../../../models/constants';
import DateService from '../../../services/date.service';
import UtilsService from '../../../services/utils.service';
export default defineComponent({
  name: 'otb-slot-selection-box',
  props: {
    status: {
      type: String as PropType<RequestSelectionBoxesStatusType>,
      default: RequestSelectionBoxesStatus.TODO,
    },
    step: {
      type: Number,
      default: 1,
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    currentStep: String,
    accordionClosingHandler: {
      type: Function,
      default: () => () => {
        return;
      },
    },
  },
  components: {
    'datetime-picker': DatetimePicker,
    calendar: Calendar,
    'text-carousel': TextCarousel,
    'calendar-period': CalendarPeriod,
  },
  setup(props, { emit }) {
    const { commit, state, getter } = useStore();
    const {
      calculateFooterTopPosition,
      footerPositionScrollEvent,
      footerTopStyle,
    } = useFooterPosition();

    const {
      getSessionStorageSelectedDay,
      getSessionStorageSelectedPeriod,
      getSessionStorageSelectedLocation,
      setSessionStorageSelectedDay,
      setSessionStorageSelectedPeriod,
    } = useRequestSessionStorage();

    const selectedDay = computed(
      () => `${state.otbcFlow.selectedDay.toISOString().slice(0, 11)}`
    );

    const getPeriodSlot = (period, slots) => {
      const samePeriodSlot = slots.find(
        (slot) => slot.value.dayPeriod === period
      );

      if (!samePeriodSlot) return slots[0];
      return samePeriodSlot;
    };

    onMounted(() => {
      footerPositionScrollEvent();
      handleSelectedDayTimeSlotsChange(getSelectedDayTimeSlots.value, null);

      const thisSelectedDay = getSessionStorageSelectedDay();
      const thisSelectedPeriod = getSessionStorageSelectedPeriod();

      if (!thisSelectedDay) return;

      const isStoredDayDifferentThanState = !thisSelectedDay.includes(
        selectedDay?.value ?? ''
      );

      const location = getSessionStorageSelectedLocation();
      const isStoredLocationDifferentThanState =
        location && location?.label !== state?.otbcFlow?.initialLocation?.label;

      if (isStoredDayDifferentThanState && !isStoredLocationDifferentThanState)
        setTimeout(toggle, 100);

      const date = new Date(thisSelectedDay);
      changeDate(date);
      changeSlot(
        getPeriodSlot(thisSelectedPeriod, requestSlotsResponse.value).value
      );

      if (isStoredLocationDifferentThanState) apply(false);
    });

    const { closeAccordion, selectionBoxIsDone, selectionBoxClass } =
      useRequestSelectionBoxes();

    const { isMq4 } = useMediaQuery();
    const Calendar = useCalendar(props, { emit });
    const { trackCalendar } = useSlotSelectionBox();

    const accordionRef = ref();
    const timeSlotsLoading = ref(true);
    const activeCalendarSlide = ref(0);

    const localeCode = computed((): string => getter.localeCode);

    const datePickerOpen = ref(false);
    const informationCardCollapsible = ref(true);
    const testDriveStatusValue = ref('');
    const datepickerModalOpen = ref(false);

    const changeCalendarSheet = (page: SlideInformation): void => {
      activeCalendarSlide.value = page.index;
    };

    const selectedSlot = ref({
      end: `${selectedDay.value}12:00+01:00`,
      label: '09:00 - 12:00',
      start: `${selectedDay.value}09:00+01:00`,
      dayPeriod: 'morning',
    });

    const selectedDates = ref({ start: new Date(state.otbcFlow.selectedDay) });

    const rangeDate = computed(() => {
      return selectedRangeDate;
    });

    const rangeTime = computed(() => {
      return selectedRangeTime;
    });

    const firstUnavailableDate = computed(() => {
      return new Date().setMonth(new Date().getMonth() + 3);
    });

    const showSuccessIcon = computed(() => {
      return selectionBoxIsDone(props.status);
    });

    const dealerCalendar = computed((): DatesAvailabilityType => {
      return getter.dealerCalendar;
    });

    const getDayRequestTimeSlots = (date: string) => {
      return state.otbcFlow.dealerCalendar?.find((day) => {
        return day.date === date;
      });
    };

    const getSelectedDayTimeSlots = computed((): any => {
      return getDayRequestTimeSlots(selectedDay.value.slice(0, -1))
        ?.requestTimeSlots?.filter((timeslot) => timeslot.start && timeslot.end)
        .map(DateService.requestTimeSlotFormat);
    });

    const requestSlotsResponse = computed((): any => {
      return getSelectedDayTimeSlots.value ?? [];
    });

    const getSelectionBoxClass = (mainClass: string) => {
      return selectionBoxClass(props.status, mainClass);
    };

    const changeSheet = (page: SlideInformation): void => {
      console.log(page);
    };

    const changeDate = async (day: Date): Promise<void> => {
      timeSlotsLoading.value = true;
      selectedDates.value.start = day;
      DatetimePickerService.commitSelectedDay(selectedDates.value.start);
      DatetimePickerService.commitSelectedPeriod(selectedSlot.value);
      trackCalendar(TRACKING_EVENT_LABEL.CALENDAR_SELECT_DATE);
      timeSlotsLoading.value = false;
    };

    const changeSlot = (slot: CalendarPeriodOption) => {
      selectedSlot.value = slot;
      DatetimePickerService.commitSelectedPeriod(selectedSlot.value);
      trackCalendar(TRACKING_EVENT_LABEL.CALENDAR_SELECT_TIME);
    };

    const apply = (shouldSetSession = true) => {
      props.accordionClosingHandler();

      if (shouldSetSession) {
        setSessionStorageSelectedDay(selectedDates.value.start);
        setSessionStorageSelectedPeriod(selectedSlot.value.dayPeriod);
      }

      commit('selectedPeriod', selectedSlot.value);
      emit('changeStatus');
      toggle();
      trackCalendar(TRACKING_EVENT_LABEL.CALENDAR_APPLY);
    };

    const boxIsOpen = computed(() => {
      const isBoxOpen = props.currentStep == RequestSelectionBoxesStep.SLOT;

      if (isBoxOpen) emit('onBoxOpen', RequestSelectionBoxesStep.SLOT);
      return isBoxOpen;
    });

    const selectedValue = computed(() => {
      const formattedDate = DateService.formatDate(
        state.configuration.locale,
        state.otbcFlow.selectedDay,
        {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        }
      )
        .split('/')
        .join('.');

      return `${formattedDate}, ${i18n.t(
        `schedule.period.${state.otbcFlow.selectedPeriod.dayPeriod}`
      )}`;
    });

    const toggle = () => {
      emit('onToggle');
      const isOpen = accordionRef.value.hasAttribute('open');
      if (!isOpen) {
        props.accordionClosingHandler();
        UtilsService.closeAllAccordions();
        emit('open', 'slot');
      }
    };

    const slotSelectionBoxLabelFont = computed(() => {
      return props.isMobile ? 'wb-type-copy-strong' : 'wb-type-heading-s';
    });

    const showToggleLabel = computed(() => {
      return (
        props.status === RequestSelectionBoxesStatus.AVAILABLE ||
        props.status === RequestSelectionBoxesStatus.TODO
      );
    });

    watch(
      () => state.otbcFlow.selectedDay,
      (day) => {
        changeDate(day);
      }
    );

    const handleSelectedDayTimeSlotsChange = (newSlots, previousSlots) => {
      if (!previousSlots) {
        if (newSlots && newSlots.length > 1) changeSlot(newSlots[0].value);
      } else {
        const samePeriodSlot = getPeriodSlot(
          state.otbcFlow.selectedPeriod.dayPeriod,
          newSlots
        );
        changeSlot(samePeriodSlot.value);
      }
    };

    watch(
      () => getSelectedDayTimeSlots.value,
      handleSelectedDayTimeSlotsChange
    );

    onUpdated((): void => {
      calculateFooterTopPosition();
    });

    return {
      activeCalendarSlide,
      datePickerOpen,
      informationCardCollapsible,
      testDriveStatusValue,
      datepickerModalOpen,
      selectedSlot,
      selectedDates,
      rangeDate,
      rangeTime,
      firstUnavailableDate,
      showSuccessIcon,
      dealerCalendar,
      requestSlotsResponse,
      boxIsOpen,
      selectedValue,
      showToggleLabel,
      accordionRef,
      getSelectionBoxClass,
      changeSheet,
      changeDate,
      changeSlot,
      closeAccordion,
      emit,
      apply,
      toggle,
      state,
      slotSelectionBoxLabelFont,
      localeCode,
      Calendar,
      changeCalendarSheet,
      isMq4,
      footerTopStyle,
    };
  },
});
</script>

<style scoped lang="scss" src="./slot-selection-box.scss"></style>
